import { Dictionary } from '@feedyou/schema'
import _ from 'lodash'
import { AnyObject, Mutable, STABLE_EMPTY_OBJECT } from '@feedyou/utils'
import { FoundEntity, FoundOccurrence, OccurrenceType } from './searchInDialogs'
import { createCompositeSearcher, SearchMode } from './createCompositeSearcher'

const KEY_MAPPER_SYMBOL = '.%key%.'

type SearchConfig = {
  type: OccurrenceType
  // Path can point to a string, or array of strings.
  // Path can also include a syntax of foo.%key%.bar, which will map over all keys of foo (array or object)
  paths: string[]
  modes?: SearchMode[]
}

export function createConfigs(params: Dictionary<OccurrenceType, string[]>): SearchConfig[] {
  return Object.entries(params).flatMap(([type, paths]): SearchConfig | SearchConfig[] => {
    switch (type as OccurrenceType) {
      case 'storage':
        return [
          { type: 'storage', paths, modes: ['lowercaseEquals'] },
          { type: 'text', paths, modes: ['includes'] }
        ]

      case 'text':
        return [
          { type: 'storage', paths, modes: ['interpolatedStorage'] },
          { type: 'text', paths, modes: ['includes'] }
        ]

      case 'dialog':
        return { type: 'dialog', paths, modes: ['strictEquals'] }

      case 'id':
        return { type: 'id', paths, modes: ['strictEquals'] }

      case 'name':
        return { type: 'name', paths, modes: ['includes'] }

      default:
        return []
    }
  })
}

export function searchMutableObject<O extends Mutable<AnyObject>>(
  object: O,
  query: string,
  entityDescriptor: FoundEntity,
  configs: SearchConfig[]
): FoundOccurrence<'partial'>[] {
  return configs
    .filter(config => searchForConfig(object, query, config))
    .map(config => ({ occurrenceType: config.type, foundOnEntity: entityDescriptor }))
}

export function searchForConfig<O extends Mutable<AnyObject>>(
  mutableObject: O,
  query: string,
  { paths, modes = ['includes'] }: Omit<SearchConfig, 'type'>
): boolean {
  if (!_.isObject(mutableObject)) {
    return false
  }

  const searchInValue = createCompositeSearcher(query, modes)

  return paths?.some(path => {
    if (path.includes(KEY_MAPPER_SYMBOL)) {
      const idx = path.indexOf(KEY_MAPPER_SYMBOL)
      const prefix = path.slice(0, idx)
      const suffix = path.slice(idx + KEY_MAPPER_SYMBOL.length)

      const baseObject = _.get(mutableObject, prefix) ?? STABLE_EMPTY_OBJECT

      return Object.values(baseObject).some(child => {
        return searchForConfig(child as Mutable<object>, query, { paths: [suffix], modes })
      })
    }

    if (searchInValue(_.get(mutableObject, path))) {
      return true
    }

    return mutableObject.mutations?.some((mutation: any) => {
      return mutation.set?.some(
        (setItem: any) => setItem.field === path && searchInValue(setItem.value)
      )
    })
  })
}
